import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Spinner } from 'react-bootstrap';
import { articleService, UPLOADS_BASE_URL } from '../services/api';
import { FaClock, FaEye, FaCalendar, FaUser, FaArrowLeft, FaMapMarkerAlt, FaCalendarAlt, FaDollarSign } from 'react-icons/fa';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import './ArticleDetailPage.css';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const categoryLabels = {
    'dive-sites': 'Dive Sites',
    'trip-reports': 'Trip Reports',
    'gear-reviews': 'Gear Reviews',
    'techniques': 'Diving Techniques',
    'safety': 'Safety',
    'beginner-guides': 'Beginner Guides',
    'destinations': 'Destinations',
    'marine-life': 'Marine Life',
    'conservation': 'Conservation',
    'news': 'News'
  };

  useEffect(() => {
    loadArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const response = await articleService.getArticleBySlug(slug);
      setArticle(response.data);
      
      // Load related articles
      if (response.data._id) {
        const relatedResponse = await articleService.getRelatedArticles(response.data._id);
        setRelatedArticles(relatedResponse.data);
      }
    } catch (error) {
      console.error('Failed to load article:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTripDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!article) {
    return (
      <Container className="py-5 text-center">
        <h2>Article Not Found</h2>
        <p className="text-muted">The article you're looking for doesn't exist.</p>
        <Link to="/blog">
          <Button variant="primary">Back to Blog</Button>
        </Link>
      </Container>
    );
  }

  return (
    <div className="article-detail-page">
      <SEO 
        title={`${article.title} | DiveConnect Blog`}
        description={article.metaDescription || article.excerpt || article.content.replace(/<[^>]*>/g, '').substring(0, 160)}
        keywords={article.metaKeywords || article.tags.join(', ')}
        image={article.featuredImage ? `${UPLOADS_BASE_URL}/uploads/${article.featuredImage}` : undefined}
        url={`https://dive-connect.com/blog/${article.slug}`}
        type="article"
        author={article.author?.name || 'DiveConnect'}
      />
      
      {/* Hero Section with Featured Image */}
      <div 
        className="article-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${UPLOADS_BASE_URL}/uploads/${article.featuredImage})`
        }}
      >
        <Container>
          <div className="article-hero-content">
            <Badge bg="info" className="mb-3">
              {categoryLabels[article.category]}
            </Badge>
            <h1>{article.title}</h1>
            <div className="article-meta-hero">
              <span className="me-4">
                <FaUser className="me-2" />
                {article.author?.name || 'Anonymous'}
              </span>
              <span className="me-4">
                <FaCalendar className="me-2" />
                {formatDate(article.publishedAt)}
              </span>
              <span className="me-4">
                <FaClock className="me-2" />
                {article.readTime} min read
              </span>
              <span>
                <FaEye className="me-2" />
                {article.views} views
              </span>
            </div>
          </div>
        </Container>
      </div>

      <Container className="my-5">
        <Breadcrumbs items={[
          { label: 'Blog', link: '/blog' },
          { label: categoryLabels[article.category] },
          { label: article.title }
        ]} />
        
        <Row>
          {/* Main Article Content */}
          <Col lg={8}>
            <div className="article-content">
              <div className="back-link mb-4">
                <Link to="/blog" className="text-decoration-none">
                  <FaArrowLeft className="me-2" />
                  Back to Blog
                </Link>
              </div>

              {/* Excerpt */}
              <div className="article-excerpt">
                <p className="lead">{article.excerpt}</p>
              </div>

              {/* Article Body */}
              <div 
                className="article-body"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="article-tags mt-4">
                  <h5>Tags:</h5>
                  {article.tags.map((tag, index) => (
                    <Badge key={index} bg="secondary" className="me-2 mb-2">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Related Trips */}
              {article.relatedTrips && article.relatedTrips.length > 0 && (
                <div className="related-trips mt-5">
                  <h3>Related Dive Trips</h3>
                  <p className="text-muted">Interested? Check out these trips!</p>
                  <Row>
                    {article.relatedTrips.map((trip) => (
                      <Col key={trip._id} md={6} className="mb-3">
                        <Card className="trip-card">
                          <Card.Img
                            variant="top"
                            src={`${UPLOADS_BASE_URL}/uploads/${trip.image}`}
                            alt={trip.name}
                            style={{ height: '150px', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.src = `${UPLOADS_BASE_URL}/uploads/default-trip.jpg`;
                            }}
                          />
                          <Card.Body>
                            <Card.Title>{trip.name}</Card.Title>
                            <div className="trip-info">
                              <small className="text-muted d-block mb-1">
                                <FaMapMarkerAlt className="me-1" />
                                {trip.location}
                              </small>
                              <small className="text-muted d-block mb-1">
                                <FaCalendarAlt className="me-1" />
                                {formatTripDate(trip.startDate)}
                              </small>
                              {trip.price && (
                                <small className="text-muted d-block mb-2">
                                  <FaDollarSign className="me-1" />
                                  From ${trip.price}
                                </small>
                              )}
                            </div>
                            <Link to={`/trips/${trip._id}`}>
                              <Button variant="primary" size="sm" className="w-100">
                                View Trip
                              </Button>
                            </Link>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </div>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            <div className="article-sidebar">
              {/* Author Info */}
              <Card className="mb-4">
                <Card.Body>
                  <h5>About the Author</h5>
                  <div className="author-info">
                    <div className="author-avatar">
                      {article.author?.name?.charAt(0) || 'A'}
                    </div>
                    <div>
                      <strong>{article.author?.name || 'Anonymous'}</strong>
                      {/* Public contact address — never the author's account email */}
                      <p className="text-muted small mb-0">
                        <a href="mailto:info@dive-connect.com">info@dive-connect.com</a>
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <Card>
                  <Card.Body>
                    <h5>Related Articles</h5>
                    {relatedArticles.map((related) => (
                      <div key={related._id} className="related-article-item">
                        <Link to={`/blog/${related.slug}`} className="text-decoration-none">
                          <img
                            src={`${UPLOADS_BASE_URL}/uploads/${related.featuredImage}`}
                            alt={related.title}
                            className="related-article-img"
                            onError={(e) => {
                              e.target.src = `${UPLOADS_BASE_URL}/uploads/default-article.jpg`;
                            }}
                          />
                          <h6>{related.title}</h6>
                          <small className="text-muted">
                            <FaClock className="me-1" />
                            {related.readTime} min read
                          </small>
                        </Link>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ArticleDetailPage;
